import { Button } from '@primer/react';
import ButtonWithTooltipWhenDisabled from '@upright/ui/common/generic/components/buttons/disabled-button-with-tooltip';
import styled from 'styled-components';

interface FormControlButtonsProps {
  isEditing: boolean;
  hasChanges: boolean;
  onEdit: () => void;
  onCancel: () => void;
  onSave: () => void;
  disabled?: boolean;
  disabledReason?: string;
  editButtonText?: string;
  /** For an editor whose save only applies the edit locally, e.g. to a proposal saved later. */
  saveButtonText?: string;
  /** Holds back Save alone, e.g. while a row is still being filled in, and leaves Cancel usable. */
  saveDisabled?: boolean;
  addButtonConfig?: {
    onAdd: () => void;
    label: string;
  };
}

/**
 * Control buttons for operational data forms
 * Displays Edit button in view mode, Cancel/Save buttons in edit mode
 */
export function FormControlButtons({
  isEditing,
  hasChanges,
  onEdit,
  onCancel,
  onSave,
  disabled = false,
  disabledReason,
  editButtonText = 'Edit table',
  saveButtonText = 'Save changes',
  saveDisabled = false,
  addButtonConfig,
}: FormControlButtonsProps) {
  if (isEditing) {
    return (
      <ButtonGroup>
        {addButtonConfig ? (
          <Button variant="default" onClick={addButtonConfig.onAdd} disabled={disabled}>
            {addButtonConfig.label}
          </Button>
        ) : undefined}
        <Button variant="default" onClick={onCancel} disabled={disabled}>
          Cancel changes
        </Button>
        <Button variant="primary" onClick={onSave} disabled={disabled || saveDisabled || !hasChanges}>
          {saveButtonText}
        </Button>
      </ButtonGroup>
    );
  }

  return (
    <ButtonWithTooltipWhenDisabled
      variant="primary"
      onClick={onEdit}
      disabled={disabled}
      disabledReason={disabledReason}
    >
      {editButtonText}
    </ButtonWithTooltipWhenDisabled>
  );
}

const ButtonGroup = styled.div`
  display: flex;
  gap: var(--space__sm);
  align-items: center;
`;
